import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useCurrency } from '@/contexts';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import { useTranslation } from '@/hooks/useTranslation';
import { ACCOUNT_GROUPS } from '@/hooks/useAccounts';
import type { Database } from '@/lib/database.types';

type Account = Database['public']['Tables']['accounts']['Row'];

interface PatrimoineDistributionProps {
  accounts: Account[];
  viewBy?: 'account' | 'type';
  isLoading?: boolean;
}

// Colors for account types
const TYPE_COLORS: Record<string, string> = {
  bankAccounts: '#3b82f6',
  livrets: '#16c47f',
  cash: '#f59e0b',
  investments: '#8b5cf6',
  realEstate: '#06b6d4',
  vehicles: '#64748b',
  watchesJewelry: '#ec4899',
  preciousMetals: '#eab308',
  crypto: '#f97316',
  pea: '#a855f7',
  otherAssets: '#94a3b8',
};

// Account-specific colors (cycle through palette)
const ACCOUNT_COLORS = [
  '#3b82f6',
  '#16c47f',
  '#8b5cf6',
  '#f59e0b',
  '#06b6d4',
  '#ec4899',
  '#f97316',
  '#14b8a6',
  '#6366f1',
  '#84cc16',
  '#a855f7',
  '#ef4444',
  '#64748b',
  '#eab308',
  '#0ea5e9',
];

interface ChartData {
  name: string;
  value: number;
  color: string;
  percent: number;
}

export function PatrimoineDistribution({
  accounts,
  viewBy = 'type',
  isLoading = false,
}: PatrimoineDistributionProps) {
  const { formatAmount, isSecretMode, currency } = useCurrency();
  const { getRate } = useExchangeRates();
  const { t } = useTranslation();

  const convertBalance = (account: Account) => {
    const acctCurrency = (account as any).currency || 'EUR';
    const rate = getRate(acctCurrency, currency) ?? 1;
    return account.balance * rate;
  };

  // Only include assets (positive values) for the distribution
  const assetAccounts = useMemo(
    () => accounts.filter((a) => a.type === 'asset' && a.balance > 0),
    [accounts]
  );

  const totalPatrimoine = useMemo(
    () => assetAccounts.reduce((sum, a) => sum + convertBalance(a), 0),
    [assetAccounts, currency, getRate]
  );

  const chartData = useMemo((): ChartData[] => {
    if (viewBy === 'account') {
      // Group by individual accounts
      return assetAccounts
        .map((account, index) => {
          const converted = convertBalance(account);
          return {
            name: account.name,
            value: converted,
            color: ACCOUNT_COLORS[index % ACCOUNT_COLORS.length],
            percent: totalPatrimoine > 0 ? (converted / totalPatrimoine) * 100 : 0,
          };
        })
        .sort((a, b) => b.value - a.value);
    }

    // Group by account type
    const typeGroups: Record<string, number> = {};

    for (const group of ACCOUNT_GROUPS.asset) {
      const groupTotal = assetAccounts
        .filter((a) => group.subtypes.includes(a.subtype as never))
        .reduce((sum, a) => sum + convertBalance(a), 0);

      if (groupTotal > 0) {
        typeGroups[group.key] = groupTotal;
      }
    }

    return Object.entries(typeGroups)
      .map(([key, value]) => ({
        name: t(`accounts.group.${key}` as any),
        value,
        color: TYPE_COLORS[key] || '#94a3b8',
        percent: totalPatrimoine > 0 ? (value / totalPatrimoine) * 100 : 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [assetAccounts, totalPatrimoine, viewBy, t, currency, getRate]);

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-4" />
        <div className="flex justify-center">
          <div className="w-32 h-32 bg-gray-200 dark:bg-gray-700 rounded-full" />
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
        <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em] mb-4">
          {t('dashboard.distribution.title' as any)}
        </p>
        <div className="text-center py-8 text-gray-400 dark:text-gray-500">
          <p className="text-sm">{t('dashboard.distribution.noAssets' as any)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
      <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em] mb-4">
        {t('dashboard.distribution.title' as any)}
      </p>

      <div className="flex items-center gap-4">
        {/* Donut Chart */}
        <div className="w-36 h-36 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="90%"
                paddingAngle={2}
                strokeWidth={0}
              >
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) return null;
                  const data = payload[0].payload as ChartData;
                  return (
                    <div
                      style={{
                        background: 'rgba(17, 24, 39, 0.95)',
                        backdropFilter: 'blur(12px)',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                        minWidth: '160px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '6px',
                        }}
                      >
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: data.color,
                            boxShadow: `0 0 6px ${data.color}80`,
                          }}
                        />
                        <span style={{ color: '#e5e7eb', fontSize: '12px', fontWeight: 500 }}>
                          {data.name}
                        </span>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          gap: '12px',
                        }}
                      >
                        <span style={{ color: '#fff', fontSize: '16px', fontWeight: 700 }}>
                          {isSecretMode ? '•••' : formatAmount(data.value)}
                        </span>
                        <span style={{ color: data.color, fontSize: '13px', fontWeight: 600 }}>
                          {data.percent.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  );
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="flex-1 space-y-2 max-h-36 overflow-y-auto">
          {chartData.slice(0, 6).map((item, index) => (
            <div key={index} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  {item.name}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
                  {isSecretMode ? '•••' : formatAmount(item.value)}
                </span>
                <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 w-10 text-right">
                  {item.percent.toFixed(0)}%
                </span>
              </div>
            </div>
          ))}
          {chartData.length > 6 && (
            <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center pt-1">
              +{chartData.length - 6} {t('dashboard.distribution.others' as any)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
